import { JourneyEntryTypes } from 'sb-lib/types';
import { JourneyVideo } from './journey-video';
import { BusinessRulesConditions } from 'sb-lib/types/business-rules-types';
import { VisitorAlertIntegrationConfig } from 'types/alert';

export interface JourneyGraphNodeAlert {
  title: string;
  body: string;
  oncePerSession: boolean;
  userIds: string[];
  userGroupIds: string[];
  integrationConfig?: VisitorAlertIntegrationConfig;
}
export interface JourneyGraphNodeQuestion<
  T extends JourneyQuestionType = JourneyQuestionType
> {
  message: string;
  type: T;
  config: JourneyQuestionConfigMap[T];
  skipTo: string | null;
  mandatory?: boolean;
  childNodes?: string[];
}
export interface JourneyGraphNodeChatMessage {
  message: string;
  asAiChat?: boolean;
}
export interface JourneyGraphNodeIntroductionMessage {
  message: string;
}
export interface JourneyGraphNodeAssignConversation {
  userGroupId: number;
}
export interface JourneyGraphNodeExpandWidget {
  delay: number;
}
export interface JourneyGraphNodeBranch {
  conditions?: BusinessRulesConditions;
  trueChildName: string;
  falseChildName: string;
}
export interface JourneyGraphNodeBranchWithConditions extends JourneyGraphNodeBranch {
  conditions: BusinessRulesConditions;
}
export interface JourneyGraphNodeSchedule {
  message: string;
  calendarEventTypeId: number;
}

export interface JourneyGraphNodeInboundBase
  extends Omit<JourneyGraphNodeBranch, 'conditions'> {
  waitTime: number;
}
export type JourneyGraphNodeInboundChat = JourneyGraphNodeInboundBase;
export type JourneyGraphNodeInboundCall = JourneyGraphNodeInboundBase;

export interface JourneyGraphNodeVideo {
  journeyVideoId: JourneyVideo['id']; // used to lookup fresh d/l from amz
  allowSkip: boolean;
  message: null | string;
  immediatelyProceed?: boolean;
}

export type JourneyGraphNodeCloseConversation = {};
export type JourneyGraphNodeClearbitReveal = {};
export type JourneyGraphNodeSixsenseCompanyDetail = {};
export interface JourneyGraphNodeUpdateStage {
  stageId: number;
}
export interface JourneyGraphNodeUpdateMetadata {
  key: string;
  value: string;
}

export enum JourneyGraphNodeType {
  alert = 'alert',
  expand_widget = 'expand_widget',
  branch = 'branch',
  clearbit_reveal = 'clearbit_reveal',
  sixsense_company_details = 'sixsense_company_details',
  question = 'question',
  chat_message = 'chat_message',
  introduction_message = 'introduction_message',
  assign_conversation = 'assign_conversation',
  schedule = 'schedule',
  inbound_chat = 'inbound_chat',
  inbound_call = 'inbound_call',
  update_metadata = 'update_metadata',
  update_stage = 'update_stage',
  close_conversation = 'close_conversation',
  video = 'video',
  unknown = 'unknown',
}

export type JourneyGraphNodeData =
  | JourneyGraphNodeAlert
  | JourneyGraphNodeExpandWidget
  | JourneyGraphNodeBranch
  | JourneyGraphNodeQuestion
  | JourneyGraphNodeChatMessage
  | JourneyGraphNodeAssignConversation
  | JourneyGraphNodeSchedule
  | JourneyGraphNodeClearbitReveal
  | JourneyGraphNodeSixsenseCompanyDetail
  | JourneyGraphNodeInboundChat
  | JourneyGraphNodeInboundCall
  | JourneyGraphNodeUpdateMetadata
  | JourneyGraphNodeUpdateStage
  | JourneyGraphNodeCloseConversation
  | JourneyGraphNodeVideo;

type JourneyGraphNodeDataMap = {
  [JourneyGraphNodeType.alert]: JourneyGraphNodeAlert;
  [JourneyGraphNodeType.expand_widget]: JourneyGraphNodeExpandWidget;
  [JourneyGraphNodeType.branch]: JourneyGraphNodeBranch;
  [JourneyGraphNodeType.clearbit_reveal]: JourneyGraphNodeClearbitReveal;
  [JourneyGraphNodeType.sixsense_company_details]: JourneyGraphNodeSixsenseCompanyDetail;
  [JourneyGraphNodeType.question]: JourneyGraphNodeQuestion;
  [JourneyGraphNodeType.chat_message]: JourneyGraphNodeChatMessage;
  [JourneyGraphNodeType.introduction_message]: JourneyGraphNodeIntroductionMessage;
  [JourneyGraphNodeType.assign_conversation]: JourneyGraphNodeAssignConversation;
  [JourneyGraphNodeType.schedule]: JourneyGraphNodeSchedule;
  [JourneyGraphNodeType.inbound_chat]: JourneyGraphNodeInboundChat;
  [JourneyGraphNodeType.inbound_call]: JourneyGraphNodeInboundCall;
  [JourneyGraphNodeType.update_metadata]: JourneyGraphNodeUpdateMetadata;
  [JourneyGraphNodeType.update_stage]: JourneyGraphNodeUpdateStage;
  [JourneyGraphNodeType.close_conversation]: JourneyGraphNodeCloseConversation;
  [JourneyGraphNodeType.video]: JourneyGraphNodeVideo;
  [JourneyGraphNodeType.unknown]: {};
};

export interface JourneyGraphNode<T extends JourneyGraphNodeType> {
  data: JourneyGraphNodeDataMap[T];
  type: T;
  order?: number;
  needsAttention?: boolean;
}

type JourneyGraphNodes = Record<string, JourneyGraphNode<JourneyGraphNodeType>>;

export interface JourneyGraph {
  root: string;
  graph: Record<string, Record<string, {}>>;
  nodes: JourneyGraphNodes;
}

export enum JourneyExpandPolicies {
  hot = 'hot',
  always = 'always',
}

export enum JourneyInitialDisplayModes {
  skeleton = 'skeleton',
  chat = 'chat',
}

export enum JourneyDefaultType {
  workingHours = 'working_hours',
  nonWorkingHours = 'non_working_hours',
  targetAccount = 'target_account',
}

export type DeviceType = 'mobile' | 'tablet' | 'desktop';

export interface JourneyVersion {
  id: number;
  versionNumber: number;
  conditions: BusinessRulesConditions;
  graph: JourneyGraph;
  repeatableCount: number;
  expandPolicy: null | JourneyExpandPolicies;
  initialDisplayMode: null | JourneyInitialDisplayModes;
  entryType: null | JourneyEntryTypes;
  defaultType: null | JourneyDefaultType;
  dateCreated: number | null;
  dateUpdated: number | null;
  deviceTypes: DeviceType[];
  shouldCancelIfConditionsUnmet: boolean;
}
export type JourneyVersionUpdate = Omit<
  JourneyVersion,
  'id' | 'dateCreated' | 'dateUpdated' | 'versionNumber'
>;

export type JourneyType = 'regular' | 'split_test' | 'split_test_variant';

export interface Journey {
  id: number;
  type: JourneyType;
  name: string;
  active: boolean;
  enabled: boolean;
  dateCreated: number | null;
  dateUpdated: number | null;
  latestVersion: JourneyVersion;
  splitTestConfig: JourneySplitTestConfig | null;
}

export type JourneySplitTestAutoDecideType =
  | 'do_nothing'
  | 'decide_winner'
  | 'decide_winner_and_end';

export type JourneySplitTestWinningMetric =
  | 'completions'
  | 'engagements'
  | 'leads_captured'
  | 'meetings_booked'
  | 'inbound_calls';

export interface JourneySplitTestConfig {
  journeyId: number;
  variantAId: number;
  variantAName: string;
  variantBId: number;
  variantBName: string;
  splitPercent: number;
  autoDecideType: JourneySplitTestAutoDecideType;
  autoDecideScheduleStart: number | null;
  autoDecideScheduleEnd: number | null;
  autoDecideWinningMetric: JourneySplitTestWinningMetric;
}

export type JourneySplitTestConfigUpdate = Omit<
  JourneySplitTestConfig,
  'journeyId' | 'variantAId' | 'variantBId'
>;

export type JourneySplitTestWinner = 'variant_a' | 'variant_b';

export interface JourneyTemplate {
  id: number;
  name: string;
  description: string;
  journeyVersion: JourneyVersion;
}

export interface PartialJourneyWithId
  extends Partial<Omit<Journey, 'id' | 'splitTestConfig'>> {
  id: number;
  splitTestWinner?: JourneySplitTestWinner | null;
  splitTestConfig: JourneySplitTestConfigUpdate | null;
}

/** Journey without un-editable or local fields */
export interface JourneyUpdate
  extends Partial<Omit<Journey, 'type' | 'latestVersion' | 'splitTestConfig'>> {
  id: number;
  latestVersion?: JourneyVersionUpdate;
  splitTestWinner?: JourneySplitTestWinner | null;
  splitTestConfig?: JourneySplitTestConfigUpdate | null;
}

export enum JourneyCategory {
  General = 'General',
  Settings = 'Settings',
  Notifications = 'Notifications',
  Integrations = 'Integrations',
  Conversation = 'Conversation',
  CRM = 'CRM',
  Routing = 'Routing',
}

export interface JourneyNodeAction {
  name: string | null;
  node: React.ReactNode;
}

export interface JourneyActionProps {
  nodeName: string;
  setAction: React.Dispatch<React.SetStateAction<JourneyNodeAction>>;
}

export interface JourneyNodeProps extends JourneyActionProps {
  analytics: JourneyAnalytics | null;
  stepLetter: number;
  stepIndex: number;
  action: JourneyNodeAction;
  setCacheKey: React.Dispatch<React.SetStateAction<number>>;
}

export enum JourneyQuestionType {
  full_name = 'full_name',
  first_name = 'first_name',
  last_name = 'last_name',
  email = 'email',
  phone = 'phone',
  choice = 'choice',
  short_answer = 'short_answer',
}

export type JourneyQuestionConfigMap = {
  [JourneyQuestionType.full_name]: { label?: string; key?: string };
  [JourneyQuestionType.first_name]: { label?: string; key?: string };
  [JourneyQuestionType.last_name]: { label?: string; key?: string };
  [JourneyQuestionType.email]: { label?: string; key?: string };
  [JourneyQuestionType.phone]: { label?: string; key?: string };
  [JourneyQuestionType.choice]: {
    key: string;
    options: JourneyQuestionChoiceOption[];
  };
  [JourneyQuestionType.short_answer]: { label?: string; key?: string };
};

export interface JourneyQuestionChoiceOption {
  label: string;
  value?: string;
}

export interface JourneyStepAnalytics {
  total: number;
  inProgress: number;
  completed: number;
  errored: number;
  stale: number;
  canceled: number;
}

export interface JourneyPathAnalytics {
  totalJourneys: number;
  totalCompletions: number;
  totalEngagements: number;
  leadsCaptured: number;
  meetingsOffered: number;
  meetingsBooked: number;
  chatsByRep: number;
  chatsByBot: number;
  inboundCalls: number;
}

export interface JourneyAnalytics extends JourneyPathAnalytics {
  steps: Record<string, JourneyStepAnalytics>;
  paths: Record<string, JourneyPathAnalytics>;
  path?: string;
}

export enum VisitorJourneyStepStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETE = 'COMPLETE',
}

export interface VisitorJourneyStep {
  id: number;
  nodeId: string;
  status: VisitorJourneyStepStatus;
  data: any;
  dateCreated: string;
  dateCompleted: string;
}

export interface VisitorJourney {
  id: number;
  visitorId: number;
  journeyId: number;
  conversationId: number;
  steps: Array<VisitorJourneyStep>;
}
